import type {ReactNode} from 'react';
import styled from 'styled-components';
import {Button, ButtonDropdown, ComboBox, DropdownHandler, EMPHASIS, Label, Text} from '@forecast-it/design-system';
import {PriorityFlag} from './PriorityBadge';
import {PRIORITY_LABELS, PRIORITY_ORDER} from '../constants/priority';
import {SlaPriorityLegend} from './SlaPriorityLegend';
import type {TicketPriority} from '../types/ticket';
import type {FilterOption} from '../constants/filterOptions';

const Row = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[300]};
`;
const Spacer = styled.div`
	flex: 1;
`;
const AssigneeField = styled.div`
	width: 220px;
`;
const TrailingControls = styled.div`
	display: flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[200]};
`;

/** Sentinel for "any assignee" — the combobox needs a key for the cleared state. */
export const ANY_ASSIGNEE = '__any__';

export interface QuickFiltersProps {
	/** The single selected priority, or null for all priorities. */
	priority: TicketPriority | null;
	onPriorityChange: (next: TicketPriority | null) => void;
	/** The selected assignee key: {@link ANY_ASSIGNEE}, a sentinel, or a person id. */
	assignee: string;
	assigneeOptions: readonly FilterOption[];
	onAssigneeChange: (next: string) => void;
	/** True when any quick filter is set, which reveals the clear action. */
	hasFilters: boolean;
	onClear: () => void;
	/**
	 * Controls that belong beside the legend rather than in the page header — the
	 * filter drawer trigger and the column picker.
	 */
	trailing?: ReactNode;
}

/**
 * The queue's at-a-glance filters: one priority pick and one assignee pick, both
 * single-select, because a queue is read one slice at a time. Everything multi-select
 * (labels, requesters, date ranges, SLA state) lives in the filter drawer reached
 * from `trailing`.
 */
export function QuickFilters({
	priority,
	onPriorityChange,
	assignee,
	assigneeOptions,
	onAssigneeChange,
	hasFilters,
	onClear,
	trailing,
}: QuickFiltersProps) {
	const priorityLabel = priority ? PRIORITY_LABELS[priority] : 'All';

	return (
		<Row data-testid="quick-filters">
			<Label>Quick filters</Label>

			<DropdownHandler>
				<DropdownHandler.Trigger>
					<ButtonDropdown emphasis={EMPHASIS.LOW} data-cy="quick-filter-priority">
						{`Priority: ${priorityLabel}`}
					</ButtonDropdown>
				</DropdownHandler.Trigger>
				<DropdownHandler.Content offset={4}>
					<DropdownHandler.Item onClick={() => onPriorityChange(null)}>All</DropdownHandler.Item>
					<DropdownHandler.Separator />
					{PRIORITY_ORDER.map(option => (
						<DropdownHandler.Item key={option} onClick={() => onPriorityChange(option)}>
							<PriorityFlag priority={option} />
							{PRIORITY_LABELS[option]}
						</DropdownHandler.Item>
					))}
				</DropdownHandler.Content>
			</DropdownHandler>

			<AssigneeField>
				{/* The design-system ComboBox takes its selection through `value` and reports
				    it through `onChange` — it maps those onto react-aria's selectedKey /
				    onSelectionChange itself, overwriting anything a caller passes under
				    those names, so passing them directly silently clears the selection.

				    It cannot be given an accessible name without a visible one: the input's
				    `aria-label` is hard-set from the `label` prop after react-aria's props
				    are spread, so a caller's own `aria-label` is always overwritten with
				    undefined, and `label` renders a visible Label above the control. The
				    design has no label here, so the input is addressed by `data-cy` — which
				    the ComboBox does forward to it. */}
				<ComboBox
					data-cy="quick-filter-assignee"
					width="flex"
					menuTrigger="focus"
					value={assignee}
					defaultItems={assigneeOptions as FilterOption[]}
					onChange={(key: string | null) => onAssigneeChange(key || ANY_ASSIGNEE)}
				>
					{(item: FilterOption) => (
						<ComboBox.Item textValue={item.label}>
							<Text>{item.label}</Text>
						</ComboBox.Item>
					)}
				</ComboBox>
			</AssigneeField>

			{hasFilters && (
				<Button
					emphasis={EMPHASIS.LOW}
					icon="closeS"
					onPress={onClear}
					data-cy="quick-filter-clear"
					data-testid="quick-filter-clear"
				>
					Clear filters
				</Button>
			)}

			<Spacer />

			<TrailingControls>
				{trailing}
				<SlaPriorityLegend />
			</TrailingControls>
		</Row>
	);
}
